import { Assignee, AssigneesListResponse } from '@/types/users';
import api, { API_PREFIX } from './api';

export const USERS_STATE_KEY = 'users';

/** Max page size accepted by the assignee list endpoint; use pagination beyond this. */
export const ASSIGNEES_PAGE_SIZE_MAX = 100;

/** Hard cap so a misbehaving API (e.g. ignores `page_number` and always returns a full page) cannot loop forever. */
const ASSIGNEES_MAX_PAGES = 100;

export type GetAssigneesQueryParams = {
  admin_org_id: string;
  /** 0-based page index — matches backend `page_number` query param. */
  page_number?: number;
  page_size?: number;
  /**
   * When the API supports it, stable ordering for pagination (e.g. chronological).
   * Omit to use the backend default; ensure that default is stable so page 1 + page 2 concatenate correctly.
   */
  order_by?: string;
  /**
   * When the API supports filtering by role, pass a single role to reduce payload.
   * To load all assignee-eligible users without this param, filter client-side or use
   * {@link fetchAssigneesForEligibleRoles} if the API documents multi-role or repeated queries.
   */
  role?: string;
};

export const getAssignees = (params: GetAssigneesQueryParams) => {
  return api.get<AssigneesListResponse>(`${API_PREFIX}/users/assignee`, {
    params,
  });
};

/**
 * Walks assignee list pages until every user is loaded. Batches are concatenated in page order;
 * the backend should use a stable sort (ideally chronological) so “first page” is meaningful.
 */
export const fetchAllAssignees = async (
  adminOrgId: string,
  extra?: Pick<GetAssigneesQueryParams, 'order_by' | 'role'>
): Promise<Assignee[]> => {
  const users: Assignee[] = [];
  const seenIds = new Set<string>();
  let pageNumber = 0;

  while (pageNumber < ASSIGNEES_MAX_PAGES) {
    const res = await getAssignees({
      admin_org_id: adminOrgId,
      page_number: pageNumber,
      page_size: ASSIGNEES_PAGE_SIZE_MAX,
      ...extra,
    });
    const body = res.data;
    const batch = body?.users ?? [];
    if (batch.length === 0) break;

    let addedNew = 0;
    for (const user of batch) {
      if (!seenIds.has(user.user_id)) {
        seenIds.add(user.user_id);
        users.push(user);
        addedNew += 1;
      }
    }

    const total = body?.total;
    if (typeof total === 'number' && users.length >= total) break;
    if (batch.length < ASSIGNEES_PAGE_SIZE_MAX) break;
    // Same rows as the previous page (API ignored `page_number` / offset) — avoid duplicates and stop.
    if (addedNew === 0) break;

    pageNumber += 1;
  }

  return users;
};

/**
 * If the API supports `role` per request, loads each eligible role in parallel (each paginated),
 * then merges by `user_id`. Skips duplicate API work once the backend can filter assignees server-side.
 * If `role` is ignored by the API, do not use this—use {@link fetchAllAssignees} once instead.
 */
export const fetchAssigneesForEligibleRoles = async (
  adminOrgId: string,
  roles: readonly string[],
  extra?: Pick<GetAssigneesQueryParams, 'order_by'>
): Promise<Assignee[]> => {
  const perRole = await Promise.all(roles.map(role => fetchAllAssignees(adminOrgId, { ...extra, role })));
  const byId = new Map<string, Assignee>();
  for (const list of perRole) {
    for (const user of list) {
      byId.set(user.user_id, user);
    }
  }
  return [...byId.values()];
};

export const getUserById = (userId: string) => {
  return api.get<Assignee>(`${API_PREFIX}/users/${userId}`);
};

const getAdminOrgId = () => {
  if (import.meta.env.VITE_ENVIRONMENT === 'prod') {
    return 'a9bd31dc-5c64-42cc-994b-d0aef5ed4cb7';
  } else if (import.meta.env.VITE_ENVIRONMENT === 'local') {
    return 'b09705a8-bddc-4b3c-8f2d-e499c783dd83';
  } else {
    return 'd7793e01-021d-447a-a452-bb846ef4860f';
  }
};

export const ADMIN_ORG_ID = getAdminOrgId();
